import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import kotlinx.coroutines.delay
import models.LimitedTimeGameModes
import models.RoundModifier
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random

/**
 * The start screen's seasonal decoration, matching the native iOS app: leaves drifting down while
 * Fall Harvest or Halloween runs, bats flapping across the upper half during Halloween, and two
 * jack-o'-lanterns in the bottom corners. Purely decorative, non-interactive, and hidden when the
 * player turns Seasonal effects off.
 */
@Composable
internal fun StartScreenSeasonalEffects(modifier: Modifier = Modifier) {
    val enabled by SeasonalEffectsPreferences.enabled.collectAsState()
    if (!enabled) return
    // Events switch at midnight UTC; re-read the shared calendar now and then while this is up.
    val event by produceState(LimitedTimeGameModes.activeRoundModifierNow()) {
        while (true) {
            delay(60_000)
            value = LimitedTimeGameModes.activeRoundModifierNow()
        }
    }
    val current = event
    if (current != RoundModifier.FALL_HARVEST && current != RoundModifier.HALLOWEEN) return
    val halloween = current == RoundModifier.HALLOWEEN

    val leaves = remember { List(14) { Leaf(Random) } }
    val bats = remember { List(5) { Bat(it, Random) } }
    var seconds by remember { mutableFloatStateOf(0f) }
    LaunchedEffect(Unit) {
        var start = 0L
        var last = 0L
        // ~30 fps is plenty for slow drifting leaves and halves the redraw cost on the start screen.
        while (true) withFrameNanos { now ->
            if (start == 0L) start = now
            if (now - last >= 33_000_000L) {
                seconds = (now - start) / 1_000_000_000f
                last = now
            }
        }
    }

    Box(modifier) {
        Canvas(Modifier.matchParentSize()) {
            val t = seconds
            val unit = density // iOS points → px
            for (leaf in leaves) drawLeaf(leaf, t, unit)
            if (halloween) {
                for (bat in bats) drawBat(bat, t, unit)
                val r = 16f * unit
                for (x in listOf(r * 1.6f, size.width - r * 1.6f)) {
                    drawPumpkin(Offset(x, size.height - r * 1.2f), r, strength = 2.4f)
                }
            }
        }
    }
}

private class Leaf(random: Random) {
    val x = random.nextFloat()
    val phase = random.nextFloat() * 2f * PI.toFloat()
    val fall = 38f + random.nextFloat() * 34f          // dp per second
    val sway = 18f + random.nextFloat() * 28f          // dp
    val swaySpeed = 0.5f + random.nextFloat() * 0.6f
    val spin = -80f + random.nextFloat() * 160f        // degrees per second
    val flutter = 1.4f + random.nextFloat() * 1.8f
    val size = 14f + random.nextFloat() * 12f          // dp
    val color = leafColors[random.nextInt(leafColors.size)]
    val oak = random.nextBoolean()
}

private val leafColors = listOf(
    Color(0xFFC0392B), Color(0xFFE67E22), Color(0xFFD35400),
    Color(0xFFF1C40F), Color(0xFFA0522D), Color(0xFFB9770E),
)

private class Bat(index: Int, random: Random) {
    // Spread through the upper half so the bats cross above the form, not through it.
    val altitude = 0.06f + index * 0.1f + (random.nextFloat() - 0.5f) * 0.06f
    val speed = 34f + random.nextFloat() * 24f         // dp per second
    val leftward = index % 2 == 0
    val phase = random.nextFloat() * 2f * PI.toFloat()
    val bob = 8f + random.nextFloat() * 10f            // dp
    val flap = 7f + random.nextFloat() * 3f            // wingbeats, radians per second
    val radius = 9f + random.nextFloat() * 4f          // dp; a bat spans ~3.6 radii
}

private const val LEAF_OPACITY = 0.85f

private fun DrawScope.drawLeaf(leaf: Leaf, t: Float, unit: Float) {
    val span = size.height + 80f * unit
    val y = ((t * leaf.fall * unit + leaf.phase * 97f * unit) % span) - 40f * unit
    val x = leaf.x * size.width + sin(t * leaf.swaySpeed + leaf.phase) * leaf.sway * unit
    val s = leaf.size * unit
    translate(x, y) {
        rotate(t * leaf.spin + leaf.phase * 57f, pivot = Offset.Zero) {
            // The flutter: the leaf turning edge-on and back as it tumbles.
            scale(scaleX = maxOf(0.18f, abs(cos(t * leaf.flutter + leaf.phase))), scaleY = 1f, pivot = Offset.Zero) {
                val shape = if (leaf.oak) oakLeaf(s) else almondLeaf(s)
                drawPath(shape, leaf.color.copy(alpha = LEAF_OPACITY))
                drawLine(
                    Color.Black.copy(alpha = 0.18f * LEAF_OPACITY),
                    Offset(0f, -s / 2 + s * 0.12f),
                    Offset(0f, s / 2 + s * 0.12f),
                    strokeWidth = unit,
                )
            }
        }
    }
}

/** A pointed, almond-shaped leaf centred on the origin. */
private fun almondLeaf(s: Float) = Path().apply {
    val h = s / 2
    moveTo(0f, -h)
    quadraticTo(h + s * 0.05f, 0f, 0f, h)
    quadraticTo(-h - s * 0.05f, 0f, 0f, -h)
    close()
}

/** A lobed, oak-style leaf centred on the origin. */
private fun oakLeaf(s: Float) = Path().apply {
    fun px(v: Float) = -s / 2 + s * v
    moveTo(px(0.5f), px(0f))
    quadraticTo(px(0.7f), px(0.02f), px(0.72f), px(0.2f))
    quadraticTo(px(0.95f), px(0.3f), px(0.8f), px(0.42f))
    quadraticTo(px(0.98f), px(0.58f), px(0.74f), px(0.64f))
    quadraticTo(px(0.8f), px(0.9f), px(0.5f), px(1f))
    quadraticTo(px(0.2f), px(0.9f), px(0.26f), px(0.64f))
    quadraticTo(px(0.02f), px(0.58f), px(0.2f), px(0.42f))
    quadraticTo(px(0.05f), px(0.3f), px(0.28f), px(0.2f))
    quadraticTo(px(0.3f), px(0.02f), px(0.5f), px(0f))
    close()
}

private fun DrawScope.drawBat(bat: Bat, t: Float, unit: Float) {
    val r = bat.radius * unit
    val margin = r * 2.5f
    val span = size.width + margin * 2
    val travelled = (t * bat.speed * unit + bat.phase * 61f * unit) % span
    val x = if (bat.leftward) size.width + margin - travelled else travelled - margin
    val y = size.height * bat.altitude + sin(t * 1.3f + bat.phase) * bat.bob * unit
    val wing = r * (0.8f + sin(t * bat.flap + bat.phase) * 0.35f)
    translate(x, y) {
        rotate(sin(t * 1.3f + bat.phase) * 8f, pivot = Offset.Zero) {
            val path = Path().apply {
                moveTo(0f, -r * 0.35f)
                lineTo(-r * 0.2f, -r * 0.8f)
                lineTo(-r * 0.4f, -r * 0.1f)
                quadraticTo(-r, -wing, -r * 1.8f, -wing)
                quadraticTo(-r * 1.5f, r * 0.7f, -r * 0.8f, r * 0.25f)
                lineTo(0f, r * 0.8f)
                lineTo(r * 0.8f, r * 0.25f)
                quadraticTo(r * 1.5f, r * 0.7f, r * 1.8f, -wing)
                quadraticTo(r, -wing, r * 0.4f, -r * 0.1f)
                lineTo(r * 0.2f, -r * 0.8f)
                close()
            }
            drawPath(path, Color(0xFF9B65DC).copy(alpha = 0.75f * LEAF_OPACITY))
        }
    }
}

/** A jack-o'-lantern; [strength] scales the in-game board's faint opacities up for this screen. */
private fun DrawScope.drawPumpkin(center: Offset, r: Float, strength: Float) {
    fun a(base: Float) = minOf(1f, base * strength)
    val (x, y) = center
    drawOval(Color(0xFFFF8E32).copy(alpha = a(0.34f)), Offset(x - r, y - r), Size(r * 2, r * 1.7f))
    drawOval(Color(0xFFFFBE68).copy(alpha = a(0.3f)), Offset(x - r * 0.5f, y - r), Size(r, r * 1.7f), style = Stroke(1.5f))
    drawLine(Color(0xFF89A85A).copy(alpha = a(0.5f)), Offset(x, y - r), Offset(x + r * 0.12f, y - r * 1.4f), r * 0.2f)
    for (ex in listOf(-r * 0.32f, r * 0.32f)) {
        drawCircle(Color(0xFF44214E).copy(alpha = a(0.6f)), r * 0.12f, Offset(x + ex, y - r * 0.3f))
    }
}
